import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../config';
import AdminNavigation from '../AdminNavigation';
import AdminErrorBoundary from './AdminErrorBoundary';
import { useAuth } from '../../context/AuthContext';
import { adminLayoutAccess } from '../../lib/roles';

const API = `${BACKEND_URL}/api`;

const AdminLayout = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminLoading, setAdminLoading] = useState(true);
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    // Wait for auth context to finish loading before checking admin access
    if (!authLoading) {
      checkAdminAccess();
    }
  }, [user, isAuthenticated, authLoading]);

  const checkAdminAccess = async () => {
    try {
      // First check if there's an adminToken (traditional admin login)
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        const response = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setAdmin(response.data);
        setAdminLoading(false);
        return;
      }

      // Is the signed-in session user an admin? (lib/roles.js adminLayoutAccess)
      if (isAuthenticated && user) {
        // Admin check (role logic: lib/roles.js adminLayoutAccess)
        const hasAdminAccess = adminLayoutAccess(user);
        
        if (hasAdminAccess) {
          // User is logged in as admin - create admin profile
          setAdmin({
            id: user.id,
            username: user.first_name || user.email.split('@')[0],
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            role: 'admin'
          });
          setAdminLoading(false);
          return;
        }
      }

      // No admin access - redirect to login
      navigate('/admin/login');
    } catch (err) {
      console.error('Auth error:', err);
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    }
  };

  // Show loading spinner while auth context or admin access is loading
  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consumer"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* V5 Admin Styles */}
      <style>{`
        .font-display { font-family: 'Playfair Display', serif; }
        .font-body { font-family: 'DM Sans', sans-serif; }
        .font-mono { font-family: 'JetBrains Mono', monospace; }
        
        .stat-card {
          position: relative;
          overflow: hidden;
        }
        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
        }
        .stat-card.purple::before {
          background: linear-gradient(180deg, #8b5cf6, #a78bfa);
        }
        .stat-card.orange::before {
          background: linear-gradient(180deg, #f97316, #fb923c);
        }
        .stat-card.teal::before {
          background: linear-gradient(180deg, #14b8a6, #2dd4bf);
        }
        .stat-card.green::before {
          background: linear-gradient(180deg, #22c55e, #4ade80);
        }
        .stat-card.yellow::before {
          background: linear-gradient(180deg, #eab308, #facc15);
        }
        .stat-card.red::before {
          background: linear-gradient(180deg, #ef4444, #f87171);
        }
        .stat-card.indigo::before {
          background: linear-gradient(180deg, #6366f1, #818cf8);
        }
        
        .user-row:hover {
          background-color: rgba(249, 115, 22, 0.03);
        }
        
        .tab-active {
          background: white;
          box-shadow: 0 2px 8px rgba(30, 27, 75, 0.08);
        }
      `}</style>

      {/* Navigation */}
      <AdminNavigation admin={admin} />

      {/* Main Content - pt-24 to account for fixed header (h-16 = 64px + padding) */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <AdminErrorBoundary>
          {children}
        </AdminErrorBoundary>
      </main>
    </div>
  );
};

export default AdminLayout;
